import SelectIngredients from 'components/customComponents/Layers/SelectIngredients';
import React, { useEffect, useState } from 'react';
import {
  checkIsValidInput,
  getIngIngType,
  GetIngredients,
  getIngredientStock,
  getIngredientType,
  getNewId,
  getStockConcVendor,
  isNullOrUndefined,
  SetIngredients,
  setRoundedValue,
} from 'utils/Global';
import EICheckBox, {
  EISelectBox,
  UnitTextBox,
} from 'components/customComponents/Layers/EICheckBox';
import { TSelectIngredient } from 'business/models/CustomModels';
import SelectStocks from 'components/customComponents/Layers/SelectStocks';
import {
  PFDocStandardIngredientsDTO,
  IngredientStockDTO,
  PFDocProteinFormulationDTO,
  PFStateDTO,
} from 'business/models/Models';
import { ELayersDefaultInput } from 'enums/ELayersView';
import RMButton from 'components/customComponents/Controls/RMButton';
import { ReactComponent as BackArrow } from 'Icons/CommandPanel/Back.svg';
import { getIngredient } from 'business/plateviewer/Generate/GenerateCommon';
import { StandardIngredientCard } from './StandardIngredientCard';
import { Spinner } from 'react-bootstrap';
import { FmlxButton, FmlxIcon } from 'fmlx-common-ui';

enum ProteinIngView {
  Ingredients,
  EditIngredient,
  SelectIngredient,
  SelectStock,
  HIGHPHVIEW = 'HIGHPH',
}

type StandardIngredientProps = {
  proteinFormulation: PFDocProteinFormulationDTO;
  updateStandardIngs: (
    stIngs: PFDocStandardIngredientsDTO[],
    deletedIng?: PFDocStandardIngredientsDTO,
  ) => void;
  globalIngredients: any;
  isReadOnly: boolean;
};

const DEFAULT_PH_TEXT = '0.0';

const StandardIngredients = ({
  proteinFormulation,
  updateStandardIngs,
  globalIngredients,
  isReadOnly,
}: StandardIngredientProps) => {
  const [contentView, setContentView] = useState<ProteinIngView>(
    ProteinIngView.Ingredients,
  );
  const [selectedIng, setSelectedIng] = useState<TSelectIngredient>(null);
  const [selectedStock, setSelectedStock] = useState<IngredientStockDTO>(null);
  const [selectIngredientType, setSelectIngredientType] = useState(0);
  const [ingredientTypes, setIngredientTypes] = useState([]);
  const [concentration, setConcentration] = useState('0.0');
  const [pH, setPH] = useState('0.0');
  const [concError, setConcError] = useState(false);
  const [phError, setPhError] = useState(false);
  const [headerText, setHeaderText] = useState('');
  const [editingIngredient, setEditingIngredient] =
    useState<PFDocStandardIngredientsDTO>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [highPHStock, setHighPHStock] = useState<IngredientStockDTO>(null);
  const pfStandardIngs = proteinFormulation.proteinFormulationStandardIngredient ?? [];

  useEffect(() => {
    if (!isNullOrUndefined(globalIngredients)) {
      SetIngredients(globalIngredients);
      setIsLoading(false);
    }
  }, [globalIngredients]);

  useEffect(() => {
    if (!isNullOrUndefined(selectedStock)) {
      getIngredientTypes();
      if (concentration === '0.0') {
        setConcentration(
          !isNullOrUndefined(selectedStock.lowConcentration) &&
            selectedStock.lowConcentration.toFixed(3),
        );
      }
    }
  }, [selectedStock]);

  useEffect(() => {
    switch (contentView) {
      case ProteinIngView.EditIngredient:
        setHeaderText('Edit Standard Ingredients');
        break;
      case ProteinIngView.SelectIngredient:
        setHeaderText('Select Standard Ingredients');
        break;
      case ProteinIngView.SelectStock:
        setHeaderText('Select Stock');
        break;
    }
  }, [contentView]);

  const AddIngredinetHandler = () => {
    setConcentration('0.0');
    setPH('0.0');
    setContentView(ProteinIngView.EditIngredient);
  };

  const selectIngredientHandler = (ing: TSelectIngredient) => {
    if (ing != selectedIng) {
      setSelectedStock(null);
      setHighPHStock(null);
    }
    setSelectedIng(ing);
    setContentView(ProteinIngView.EditIngredient);
  };

  const selectStockHandler = (stock: IngredientStockDTO) => {
    setSelectedStock(stock);
    setContentView(ProteinIngView.EditIngredient);
  };
  const selectHighPhStockHandler = (stock: IngredientStockDTO) => {
    setHighPHStock(stock);
    setContentView(ProteinIngView.EditIngredient);
  };

  function changeIngredientType(e) {
    setSelectIngredientType(Number(e.target.value));
  }

  function getIngredientTypes() {
    if (isNullOrUndefined(selectedStock)) {
      return;
    }
    const selectIngIngType = getIngIngType(selectedIng.id);
    const ingType = getIngredientType(selectIngIngType.ingredientTypeId);
    if (ingType.isBuffer) {
      const ingType = GetIngredients().ingredientType.filter(
        (ingType) => ingType.id === selectIngIngType.ingredientTypeId,
      );
      setIngredientTypes(
        ingType.map((ingtype) => {
          return { name: ingtype.name, value: ingtype.id };
        }),
      );
    } else {
      const ingingtypes = GetIngredients().ingredientIngredientType.filter(
        (ingingtype) => ingingtype.ingredientId === selectIngIngType.ingredientId,
      );
      const ingType = ingingtypes.map((ingingtype) =>
        GetIngredients().ingredientType.find(
          (ingtype) => ingtype.id === ingingtype.ingredientTypeId,
        ),
      );
      setIngredientTypes(
        ingType.map((ingtype) => {
          return { name: ingtype.name, value: ingtype.id };
        }),
      );
    }
  }

  function handleConcentration(e) {
    let concValue = e.target.value;
    const isValid = checkIsValidInput(concValue);
    if (concValue == '.') {
      concValue = '0.';
    }
    if (isValid) {
      setRoundedValue(concValue, setConcentration);
      if (
        isNullOrUndefined(selectedStock) ||
        concValue > selectedStock.stockConcentration ||
        e.target.value === ''
      ) {
        setConcError(true);
      } else {
        setConcError(false);
      }
    }
  }

  function handlePH(e) {
    let pHValue = e.target.value;
    const isValid = checkIsValidInput(pHValue);
    if (pHValue == '.') {
      pHValue = '0.';
    }
    if (isValid) {
      setRoundedValue(pHValue, setPH);
      if (
        !selectedStock ||
        pHValue < selectedStock.stockPh ||
        pHValue > highPHStock?.stockPh ||
        e.target.value === ''
      ) {
        setPhError(true);
      } else {
        setPhError(false);
      }
    }
  }

  const applyIngredient = () => {
    const pfIngredient: PFDocStandardIngredientsDTO = new PFDocStandardIngredientsDTO();
    if (editingIngredient) {
      pfIngredient.id = editingIngredient.id;
      pfIngredient.sIngState = editingIngredient.sIngState;
      setEditingIngredient(null);
      if (pfIngredient.sIngState != PFStateDTO.New) {
        pfIngredient.sIngState = PFStateDTO.Modified;
      }
    } else {
      pfIngredient.id = getNewId();
      pfIngredient.sIngState = PFStateDTO.New;
    }
    pfIngredient.ingredientStockId = selectedStock.id;
    pfIngredient.ingredientIngredientTypeId = selectedIng.id;
    pfIngredient.concentration = parseFloat(concentration);
    pfIngredient.pH = parseFloat(pH);
    pfIngredient.highPhingredientStockId = highPHStock?.id;
    const otherIngs = editingIngredient
      ? pfStandardIngs.filter((i) => i.id != editingIngredient.id)
      : pfStandardIngs;
    updateStandardIngs([...otherIngs, pfIngredient]);
    setContentView(ProteinIngView.Ingredients);
    setSelectedIng(null);
    setSelectedStock(null);
    setHighPHStock(null);
  };

  function backNavHandler(e) {
    switch (contentView) {
      case ProteinIngView.EditIngredient:
        setContentView(ProteinIngView.Ingredients);
        setEditingIngredient(null);
        setSelectedIng(null);
        setSelectedStock(null);
        setHighPHStock(null);
        break;
      case ProteinIngView.SelectIngredient:
      case ProteinIngView.SelectStock:
        setContentView(ProteinIngView.EditIngredient);
        break;
      case ProteinIngView.HIGHPHVIEW:
        setContentView(ProteinIngView.EditIngredient);
        break;
    }
  }

  const deleteIngredient = (ingId: number) => {
    const ingToDelete = pfStandardIngs.find((i) => i.id == ingId);
    const deletedIng = PFDocStandardIngredientsDTO.fromJS({
      ...ingToDelete,
      sIngState: PFStateDTO.Deleted,
    });
    updateStandardIngs(
      pfStandardIngs.filter((i) => i.id != ingId),
      deletedIng,
    );
  };

  function editIngredient(ing: PFDocStandardIngredientsDTO) {
    setEditingIngredient(ing);
    const ingIngType = getIngIngType(ing.ingredientIngredientTypeId);
    const ingName = getIngredient(ingIngType.ingredientId).longName;
    setSelectedIng({ id: ing.ingredientIngredientTypeId, name: ingName });
    setSelectedStock(getIngredientStock(ing.ingredientStockId));
    setHighPHStock(getIngredientStock(ing.highPhingredientStockId));
    setConcentration(ing.concentration.toString());
    setPH(ing.pH?.toString() ?? DEFAULT_PH_TEXT);
    setContentView(ProteinIngView.EditIngredient);
  }

  const getPhInputFieldLabel = () => {
    const startPh = selectedStock ? selectedStock.stockPh : 'N/A';
    const stopPh = highPHStock ? highPHStock.stockPh : 'N/A';
    return `pH (${startPh}-${stopPh})`;
  };

  return (
    <div
      id="standardingredients-wrapper"
      className="w-[100%] h-[100%] border-l border-r-0 border-b-0 border-t-0 border-solid border-[#737373]"
    >
      <div
        id="standardingredients-header"
        className="w-[100%] h-[8.66%] bg-[#F5F5F5] pl-[8px] text-[0.923rem] text-[#1B1B1B] place-content-center font-bold"
      >
        Standard Ingredient
      </div>
      <div id="standardingredients-content" className="p-2 h-[91.33%]">
        {contentView !== ProteinIngView.Ingredients && (
          <p id="standardingredients-headernav" style={{ color: '#F7A246' }}>
            <BackArrow id="standardingredients-backarrow" onClick={backNavHandler} />
            &nbsp;&nbsp;{headerText}
          </p>
        )}
        {contentView === ProteinIngView.Ingredients && (
          <>
            {!isLoading ? (
              <>
                <div
                  id="standardingredients-ingredientview"
                  className="h-[100%] flex flex-col justify-between"
                >
                  <div
                    id="standardingredients-addbutton-container"
                    className="h-[5.68%] w-[100%] justify-items-center"
                  >
                    <FmlxButton
                      id="standardingredients-addbutton"
                      label="ADD INGREDIENTS"
                      withIcon={FmlxButton.WithIcon.START}
                      icon={<FmlxIcon name="AddCircle" />}
                      type={FmlxButton.Type.POSITIVE}
                      onClick={AddIngredinetHandler}
                      disabled={isReadOnly}
                    />
                  </div>

                  <div
                    id="standardingredients-list"
                    className="h-[92.5%] w-[100%] overflow-auto"
                  >
                    {pfStandardIngs.map((ing) => {
                      if (ing.sIngState == PFStateDTO.Deleted) {
                        return;
                      }
                      return (
                        <StandardIngredientCard
                          key={`ingCard${ing.id}`}
                          ingredientData={ing}
                          onEditIngredient={() => editIngredient(ing)}
                          onDeleteIngredient={() => deleteIngredient(ing.id)}
                          isReadOnly={isReadOnly}
                        />
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div
                id="standardingredients-loading"
                className="standard-ingredients-loading-spinner"
              >
                <Spinner
                  id="standardingredients-spinner"
                  animation="border"
                  variant="secondary"
                />
              </div>
            )}
          </>
        )}

        {contentView === ProteinIngView.EditIngredient && (
          <div id="standardingredients-editview" className="p-2">
            <EICheckBox
              labelText="Name"
              textBoxText={
                selectedIng ? selectedIng.name : ELayersDefaultInput.SELECTINGREDIENT
              }
              active={!selectedIng}
              onClick={() => {
                setContentView(ProteinIngView.SelectIngredient);
              }}
            />

            <EICheckBox
              labelText={
                getIngredientType(getIngIngType(selectedIng?.id)?.ingredientTypeId)
                  ?.isBuffer
                  ? 'Low pH'
                  : 'Stock'
              }
              textBoxText={
                isNullOrUndefined(selectedStock)
                  ? ELayersDefaultInput.SELECTSTOCKS
                  : getStockConcVendor(selectedStock, true)
              }
              active={!selectedStock && selectedIng ? true : false}
              onClick={() => {
                if (!isNullOrUndefined(selectedIng)) {
                  setContentView(ProteinIngView.SelectStock);
                }
              }}
            />

            {getIngredientType(getIngIngType(selectedIng?.id)?.ingredientTypeId)
              ?.isBuffer &&
              selectedStock && (
                <EICheckBox
                  labelText="High pH"
                  textBoxText={
                    !highPHStock
                      ? ELayersDefaultInput.SELECTSTOCKS
                      : getStockConcVendor(highPHStock, true)
                  }
                  active={!highPHStock}
                  onClick={() => {
                    if (selectedIng) {
                      setContentView(ProteinIngView.HIGHPHVIEW);
                    }
                  }}
                />
              )}

            <EISelectBox
              placeholder="Select Ingredient Type"
              labelText="Type"
              selectedValue={selectIngredientType}
              onChange={changeIngredientType}
              options={ingredientTypes}
              disabled={ingredientTypes.length <= 1}
            />
            <UnitTextBox
              unit={selectedStock ? selectedStock.concentrationUnits : ''}
              labelText="Conc."
              inputValue={concentration}
              onChange={handleConcentration}
              error={concError}
              isDisabled={
                isNullOrUndefined(selectedIng) || isNullOrUndefined(selectedStock)
              }
            />

            {getIngredientType(getIngIngType(selectedIng?.id)?.ingredientTypeId)
              ?.isBuffer ? (
              <UnitTextBox
                unit=""
                labelText={getPhInputFieldLabel()}
                inputValue={pH}
                onChange={handlePH}
                error={phError}
                labelStyle={{ width: '50%' }}
              />
            ) : null}

            <div
              id="standardingredients-applybutton-container"
              style={{
                minHeight: '3rem',
                maxHeight: '3rem',
                height: '3rem',
                lineHeight: '1.375rem',
                width: '5.125rem',
                borderRadius: '0.375rem 0.5rem 0.5rem 0.375rem',
                float: 'right',
              }}
            >
              <RMButton
                id="standardingredients-applybutton"
                disabled={
                  concError ||
                  isNullOrUndefined(selectedStock) ||
                  isNullOrUndefined(selectedStock) ||
                  phError
                }
                onClick={applyIngredient}
                label="Apply"
                fullWidth={true}
              ></RMButton>
            </div>
          </div>
        )}
        {contentView === ProteinIngView.SelectIngredient && (
          <SelectIngredients
            id="standardingredients-selectingredient"
            setContentView={() => {}}
            setSelectIngredient={selectIngredientHandler}
          />
        )}
        {contentView === ProteinIngView.SelectStock && (
          <SelectStocks
            id="standardingredients-selectstock"
            setContentView={() => {}}
            setSelectStock={selectStockHandler}
            selectIngredient={selectedIng}
          />
        )}
        {contentView === ProteinIngView.HIGHPHVIEW && (
          <SelectStocks
            id="standardingredients-selecthighphstock"
            setContentView={() => {}}
            setSelectStock={selectHighPhStockHandler}
            selectIngredient={selectedIng}
            lowPHStocks={selectedStock}
          />
        )}
      </div>
    </div>
  );
};

export default StandardIngredients;
